import React, { useState } from "react";
import { BASE_URL_IMG, BASE_URL } from "../../Config/api.config";
import useLocalStorage from "../../Hooks/useLocalStorage";
import {
  CircularProgress,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TablePagination,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  MonetizationOn as MoneyIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Category as CategoryIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const WorkerInfo = ({ profile, onBack }) => {
  const [adminData] = useLocalStorage("adminData", "");
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [addCoinsAmount, setAddCoinsAmount] = useState("");
  const [removeCoinsAmount, setRemoveCoinsAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState({});
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);
  const [modalTab, setModalTab] = useState(0);
  const [description, setDescription] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [sendNotification, setSendNotification] = useState(false);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  if (!profile || !profile.basic_info) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No profile data available
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Box>
    );
  }

  // Extract data from the nested structure
  const userData = profile.basic_info.data?.[0] || {};
  const workerProfile = profile.basic_info.workerProfile?.[0] || {};
  const workerCategoryList = profile.basic_info.workerCategoryList || [];
  const workerSkills = profile.basic_info.workerSkills || [];
  const workerEducation = profile.basic_info.workerEducation || [];
  const workerLanguage = profile.basic_info.workerLanguage || [];
  const workerExperience = profile.basic_info.workerExperience || [];
  const workerProfileTime = profile.basic_info.workerProfileTime || [];
  const score = profile.basic_info.score || {};
  const walletBalance = profile.basic_info.walletBalance || 0;

  // Get statistics from the profile response
  const statistics = profile.statistics || {};

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy HH:mm");
    } catch (e) {
      return dateString;
    }
  };

  const handleWalletClick = async () => {
    setLoading(true);
    try {
      // Fetch transaction history for this client
      const response = await fetch(
        `${BASE_URL}adm.flexicoins?client_id=${userData.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminData.uid}`,
          },
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setTransactions(data.data.transactions || []);
        setShowWalletModal(true);
        setModalTab(0); // Default to transactions tab
      } else {
        showSnackbar(data.message || "Failed to load wallet data", "error");
      }
    } catch (error) {
      console.error("Failed to fetch wallet data:", error);
      showSnackbar("Failed to load wallet data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionHistory = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}adm.flexicoins?client_id=${userData.id}&limit=100`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminData.uid}`,
          },
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setTransactions(data.data.transactions || []);
      }
    } catch (error) {
      console.error("Failed to fetch transaction history:", error);
    }
  };

  const handleAddCoins = async () => {
    if (!addCoinsAmount || parseInt(addCoinsAmount) <= 0) {
      showSnackbar("Please enter a valid amount", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        client_id: userData.id,
        flexicoins: parseInt(addCoinsAmount),
        action: "add",
        description: description || `Admin added ${addCoinsAmount} flexicoins`,
        admin_note: adminNote,
        send_notification: sendNotification,
      };

      const response = await fetch(`${BASE_URL}adm.flexicoins`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === "success") {
        showSnackbar(
          `Successfully added ${addCoinsAmount} flexicoins`,
          "success"
        );
        // Refresh transaction history
        await fetchTransactionHistory();
        // Reset form
        setAddCoinsAmount("");
        setDescription("");
        setAdminNote("");
        setSendNotification(false);
        setModalTab(0); // Switch back to transactions tab
      } else {
        showSnackbar(data.message || "Failed to add flexicoins", "error");
      }
    } catch (error) {
      console.error("Add coins error:", error);
      showSnackbar("Failed to add flexicoins", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoins = async () => {
    if (!removeCoinsAmount || parseInt(removeCoinsAmount) <= 0) {
      showSnackbar("Please enter a valid amount", "error");
      return;
    }

    if (parseInt(removeCoinsAmount) > walletBalance) {
      showSnackbar(
        `Cannot remove more than current balance (${walletBalance})`,
        "error"
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        client_id: userData.id,
        flexicoins: parseInt(removeCoinsAmount),
        action: "remove",
        description:
          description || `Admin removed ${removeCoinsAmount} flexicoins`,
        admin_note: adminNote,
        send_notification: sendNotification,
      };

      const response = await fetch(`${BASE_URL}admin-flexicoins.php`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status === "success") {
        showSnackbar(
          `Successfully removed ${removeCoinsAmount} flexicoins`,
          "success"
        );
        // Refresh transaction history
        await fetchTransactionHistory();
        // Reset form
        setRemoveCoinsAmount("");
        setDescription("");
        setAdminNote("");
        setSendNotification(false);
        setModalTab(0); // Switch back to transactions tab
      } else {
        showSnackbar(data.message || "Failed to remove flexicoins", "error");
      }
    } catch (error) {
      console.error("Remove coins error:", error);
      showSnackbar("Failed to remove flexicoins", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTransactionPageChange = (event, newPage) => {
    setTransactionPage(newPage);
  };

  const handleTransactionRowsPerPageChange = (event) => {
    setTransactionRowsPerPage(parseInt(event.target.value, 10));
    setTransactionPage(0);
  };

  const getActionColor = (action) => {
    switch (action) {
      case "add":
        return "success";
      case "remove":
        return "error";
      case "purchase":
        return "info";
      default:
        return "default";
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "add":
        return <AddCircleIcon fontSize="small" />;
      case "remove":
        return <RemoveCircleIcon fontSize="small" />;
      case "purchase":
        return <MoneyIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const handleCloseModal = () => {
    setShowWalletModal(false);
    setModalTab(0);
    setAddCoinsAmount("");
    setRemoveCoinsAmount("");
    setDescription("");
    setAdminNote("");
    setSendNotification(false);
  };

  console.log(userData);
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        variant="outlined"
        sx={{ mb: 3 }}
      >
        Back to Workers List
      </Button>

      <Grid container spacing={3}>
        {/* Left Column - Profile Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar
                src={
                  userData.profile_image
                    ? `${BASE_URL_IMG}${userData.profile_image}`
                    : undefined
                }
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  bgcolor: "primary.main",
                }}
              >
                {userData.first_name?.[0]}
                {userData.last_name?.[0]}
              </Avatar>

              <Typography variant="h5" gutterBottom>
                {userData.first_name} {userData.last_name}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {userData.email}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ mb: 2 }}
              >
                <Chip
                  label={userData.active === "1" ? "Active" : "Inactive"}
                  color={userData.active === "1" ? "success" : "default"}
                  size="small"
                />
                <Chip
                  label={userData.validated === "1" ? "Verified" : "Unverified"}
                  color={userData.validated === "1" ? "success" : "warning"}
                  size="small"
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {userData.city || "N/A"},{" "}
                    {userData.country_code_name ||
                      userData.country_code ||
                      "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Joined:{" "}
                    {formatDate(score.date_joined || userData.date_created)}
                  </Typography>
                </Box>
              </Stack>

              {/* Wallet Button */}
              <Button
                variant="contained"
                startIcon={<WalletIcon />}
                onClick={handleWalletClick}
                sx={{ mt: 3 }}
                disabled={loading}
                fullWidth
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Wallet: ${walletBalance} coins`
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Statistics Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Closed Jobs
                  </Typography>
                  <Typography variant="h6">
                    {statistics.closed_jobs ||
                      score.totalConnectionsTerminated ||
                      0}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Applied Jobs
                  </Typography>
                  <Typography variant="h6">
                    {statistics.applied_jobs || 0}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Average Rating
                  </Typography>
                  <Typography variant="h6">{score.avgScore || 0}/5</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Profile Completion
                  </Typography>
                  <Typography variant="h6">
                    {profile.profile_completion || 0}%
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Details */}
        <Grid item xs={12} md={8}>
          {/* Profile Info Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PersonIcon color="primary" />
                Profile Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Job Title
                  </Typography>
                  <Typography variant="body1">
                    {workerProfile.title || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Hourly Rate
                  </Typography>
                  <Typography variant="body1">
                    {workerProfile.budget
                      ? `$${workerProfile.budget}/${
                          workerProfile.currency_code || "hr"
                        }`
                      : "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Overview
                  </Typography>
                  <Typography variant="body1">
                    {workerProfile.overview || "No overview provided"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Skills & Languages Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <BuildIcon color="primary" />
                    Skills
                  </Typography>
                  <Stack spacing={1}>
                    {workerSkills.length > 0 ? (
                      workerSkills.map((skill) => (
                        <Chip
                          key={skill.skill_id}
                          label={skill.name}
                          size="small"
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No skills listed
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <LanguageIcon color="primary" />
                    Languages
                  </Typography>
                  <Stack spacing={1}>
                    {workerLanguage.length > 0 ? (
                      workerLanguage.map((lang) => (
                        <Chip
                          key={lang.worker_language_id}
                          label={`${lang.language_name} (${lang.languale_level_name})`}
                          size="small"
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No languages listed
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Categories & Schedule Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <CategoryIcon color="primary" />
                    Categories
                  </Typography>
                  <Stack spacing={1}>
                    {workerCategoryList.length > 0 ? (
                      workerCategoryList.map((cat, index) => (
                        <Chip
                          key={index}
                          label={cat.name}
                          size="small"
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No categories listed
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <ScheduleIcon color="primary" />
                    Availability
                  </Typography>
                  <Stack spacing={1}>
                    {workerProfileTime.length > 0 ? (
                      workerProfileTime.map((time) => (
                        <Typography
                          key={time.worker_time_profile_id}
                          variant="body2"
                        >
                          {time.dayname}: {time.hour_from?.slice(0, 5)} -{" "}
                          {time.hour_to?.slice(0, 5)}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No schedule available
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Education & Experience */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <SchoolIcon color="primary" />
                    Education
                  </Typography>
                  {workerEducation.length > 0 ? (
                    workerEducation.map((edu) => (
                      <Box
                        key={edu.id}
                        sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}
                      >
                        <Typography variant="subtitle2">
                          {edu.institution_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {edu.degree}{" "}
                          {edu.area_of_study ? `in ${edu.area_of_study}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {edu.start_date} - {edu.end_date || "Present"}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No education information
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <BusinessIcon color="primary" />
                    Experience
                  </Typography>
                  {workerExperience.length > 0 ? (
                    workerExperience.map((exp) => (
                      <Box
                        key={exp.id}
                        sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}
                      >
                        <Typography variant="subtitle2">
                          {exp.company_name}
                        </Typography>
                        <Typography variant="body2">{exp.job_title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {exp.city}, {exp.country_code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {exp.start_date} - {exp.end_date || "Present"}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No experience information
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Wallet Modal */}
      <Dialog
        open={showWalletModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WalletIcon />
            <Typography variant="h6">
              Flexicoins Management - {userData.first_name} {userData.last_name}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Paper
              sx={{
                p: 2,
                bgcolor: "primary.light",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="h6" align="center">
                Current Balance: {walletBalance} coins
              </Typography>
            </Paper>
          </Box>

          <Tabs
            value={modalTab}
            onChange={(e, newValue) => setModalTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab icon={<HistoryIcon />} label="Transaction History" />
            <Tab icon={<AddCircleIcon />} label="Add Flexicoins" />
            <Tab icon={<RemoveCircleIcon />} label="Remove Flexicoins" />
          </Tabs>

          {/* Transaction History Tab */}
          {modalTab === 0 && (
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="subtitle1">
                  Transaction History ({transactions.length} records)
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={fetchTransactionHistory}
                  size="small"
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : transactions.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Admin Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions
                        .slice(
                          transactionPage * transactionRowsPerPage,
                          transactionPage * transactionRowsPerPage +
                            transactionRowsPerPage
                        )
                        .map((txn) => (
                          <TableRow key={txn.id}>
                            <TableCell>{formatDate(txn.created_at)}</TableCell>
                            <TableCell>
                              <Chip
                                icon={getActionIcon(txn.action)}
                                label={txn.action}
                                color={getActionColor(txn.action)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                color={
                                  txn.action === "add"
                                    ? "success.main"
                                    : "error.main"
                                }
                                fontWeight="bold"
                              >
                                {txn.action === "add" ? "+" : "-"}
                                {txn.flexicoins}
                              </Typography>
                            </TableCell>
                            <TableCell>{txn.description || "—"}</TableCell>
                            <TableCell>{txn.admin_note || "—"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={transactions.length}
                    rowsPerPage={transactionRowsPerPage}
                    page={transactionPage}
                    onPageChange={handleTransactionPageChange}
                    onRowsPerPageChange={handleTransactionRowsPerPageChange}
                  />
                </TableContainer>
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{ py: 4, textAlign: "center" }}
                >
                  No transaction history found
                </Typography>
              )}
            </Box>
          )}

          {/* Add Flexicoins Tab */}
          {modalTab === 1 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Add Flexicoins to {userData.first_name}'s Account
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={addCoinsAmount}
                    onChange={(e) => setAddCoinsAmount(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption">coins</Typography>
                      ),
                    }}
                    helperText="Enter the number of flexicoins to add"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., Promotional credit, Bonus, etc."
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admin Note (Optional)"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Internal note for reference"
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckIcon color="action" />
                      <Typography variant="body2">
                        Send notification email to user
                      </Typography>
                      <Button
                        size="small"
                        variant={sendNotification ? "contained" : "outlined"}
                        onClick={() => setSendNotification(!sendNotification)}
                      >
                        {sendNotification ? "Enabled" : "Disabled"}
                      </Button>
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                  >
                    <Button onClick={() => setModalTab(0)}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<AddIcon />}
                      onClick={handleAddCoins}
                      disabled={!addCoinsAmount || loading}
                    >
                      {loading ? "Adding..." : "Add Flexicoins"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Remove Flexicoins Tab */}
          {modalTab === 2 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Remove Flexicoins from {userData.first_name}'s Account
              </Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                Current balance: {walletBalance} coins. You cannot remove more
                than this amount.
              </Alert>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={removeCoinsAmount}
                    onChange={(e) => setRemoveCoinsAmount(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption">coins</Typography>
                      ),
                    }}
                    helperText={`Maximum: ${walletBalance} coins`}
                    error={parseInt(removeCoinsAmount) > walletBalance}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., Refund adjustment, Account correction, etc."
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Admin Note (Optional)"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Internal note for reference"
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckIcon color="action" />
                      <Typography variant="body2">
                        Send notification email to user
                      </Typography>
                      <Button
                        size="small"
                        variant={sendNotification ? "contained" : "outlined"}
                        onClick={() => setSendNotification(!sendNotification)}
                      >
                        {sendNotification ? "Enabled" : "Disabled"}
                      </Button>
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                  >
                    <Button onClick={() => setModalTab(0)}>Cancel</Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<RemoveIcon />}
                      onClick={handleRemoveCoins}
                      disabled={
                        !removeCoinsAmount ||
                        loading ||
                        parseInt(removeCoinsAmount) > walletBalance
                      }
                    >
                      {loading ? "Removing..." : "Remove Flexicoins"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerInfo;
